"use client";
import SkillIcon from "./SkillIcon";
import { motion } from "motion/react";
import { useFilter } from "@/app/context/FilterContext";

export interface SkillData {
  id: number;
  name: string;
  svgData: string | null;
  color: string;
  invert: boolean;
  filters: { id: number; name: string }[];
}

export interface CategoryData {
  id: number;
  name: string;
  skills: SkillData[];
}

interface Props {
  className?: string;
  categories: CategoryData[];
}

const DEFAULT_ICON = "/Icons/Template/programming-code-signs-svgrepo-com.svg";

const Skills = ({ className = "", categories }: Props) => {
  const { activeFilter } = useFilter();

  className =
    className +
    " flex flex-col bg-(--background2) mx-[4%] rounded-[16px] justify-evenly items-center";

  const skillGroupCN = "p-1 mb-1 w-full";
  const skillHeaderCN = "mt-2 mx-3 text-2xl text-(--uforeground)";
  const skillGridCN = "flex flex-wrap gap-2";

  return (
    <motion.div
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1, transition: { type: "spring", duration: 0.5 } }}
      viewport={{ once: true, amount: 1 }}
      className={className}
    >
      <div className="mb-2">
        <h1 className="text-5xl">Skills</h1>
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{
            scaleX: 1,
            transition: { duration: 0.7, delay: 0.4 },
          }}
          viewport={{ once: true }}
          className="bg-(--uforeground2) items-center w-auto h-[3px] mb-2"
          style={{ transformOrigin: "center" }}
        />
      </div>

      {categories.map((cat) =>
        cat.skills.length === 0 ? null : (
          <div key={cat.id} className={skillGroupCN}>
            <h1 className={skillHeaderCN}>{cat.name}</h1>
            <div className={skillGridCN}>
              {cat.skills.map((skill) => {
                const filterMatch = activeFilter
                  ? skill.filters.some((f) => f.name === activeFilter)
                  : null;
                return (
                  <SkillIcon
                    key={skill.id}
                    imageIcon={
                      skill.svgData
                        ? `data:image/svg+xml,${encodeURIComponent(skill.svgData)}`
                        : DEFAULT_ICON
                    }
                    shadowColor={skill.color}
                    invert={skill.invert || !skill.svgData}
                    filterMatch={filterMatch}
                  >
                    {skill.name}
                  </SkillIcon>
                );
              })}
            </div>
          </div>
        )
      )}
    </motion.div>
  );
};

export default Skills;
